/**
 * Friends Panel Component
 * Server-backed friend system for the nightclub
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { X, Users, UserPlus, UserCheck, UserX, Circle, Search, MessageCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { getToken } from '../../utils/tokenStorage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FriendsPanel = ({ isOpen, onClose, user, isGuest, onStartChat, players }) => {
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState([]);
  const [onlineFriends, setOnlineFriends] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('friends');
  const [searchQuery, setSearchQuery] = useState('');

  const token = getToken();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchFriends = useCallback(async () => {
    if (!token || isGuest) return;
    try {
      setLoading(true);
      const [friendsRes, requestsRes, onlineRes] = await Promise.all([
        fetch(`${API_URL}/api/friends`, { headers }).then(r => r.json()),
        fetch(`${API_URL}/api/friends/requests`, { headers }).then(r => r.json()),
        fetch(`${API_URL}/api/friends/online`, { headers }).then(r => r.json())
      ]);
      setFriends(Array.isArray(friendsRes) ? friendsRes : []);
      setRequests(Array.isArray(requestsRes) ? requestsRes : []);
      setOnlineFriends(Array.isArray(onlineRes) ? onlineRes : []);
    } catch (err) {
      console.error('Failed to fetch friends:', err);
    } finally {
      setLoading(false);
    }
  }, [token, isGuest]);

  useEffect(() => {
    if (isOpen && !isGuest) {
      fetchFriends();
    }
  }, [isOpen, isGuest, fetchFriends]);

  const sendFriendRequest = async (targetId) => {
    try {
      const res = await fetch(`${API_URL}/api/friends/request`, {
        method: 'POST', headers,
        body: JSON.stringify({ user_id: targetId })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || 'Friend request sent!');
      } else {
        toast.error(data.detail || 'Failed to send request');
      }
    } catch {
      toast.error('Failed to send friend request');
    }
  };

  const respondToRequest = async (friendshipId, accept) => {
    try {
      const res = await fetch(`${API_URL}/api/friends/${friendshipId}/respond`, {
        method: 'PUT', headers,
        body: JSON.stringify({ accept })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        fetchFriends();
      } else {
        toast.error(data.detail || 'Failed to respond');
      }
    } catch {
      toast.error('Failed to respond to request');
    }
  };

  const removeFriend = async (friendshipId) => {
    try {
      const res = await fetch(`${API_URL}/api/friends/${friendshipId}`, {
        method: 'DELETE', headers
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        fetchFriends();
      } else {
        toast.error(data.detail || 'Failed to remove friend');
      }
    } catch {
      toast.error('Failed to remove friend');
    }
  };

  const isOnline = (userId) => onlineFriends.some(f => f.user_id === userId);
  const getOnlineRoom = (userId) => {
    const online = onlineFriends.find(f => f.user_id === userId);
    return online?.room || null;
  };

  // Get nearby players that aren't already friends
  const nearbyPlayers = Object.entries(players || {})
    .filter(([id]) => id !== user?.user_id && !friends.some(f => f.user_id === id))
    .map(([id, p]) => ({ user_id: id, name: p.name || 'Unknown' }))
    .filter(p => !searchQuery || p.name.toLowerCase().includes(searchQuery.toLowerCase()));

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[9990] flex items-center justify-center bg-black/80 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="friends-panel-overlay"
    >
      <Card className="w-full max-w-md bg-gray-900 border-cyan-500/30 max-h-[85vh] flex flex-col" data-testid="friends-panel">
        <CardHeader className="flex flex-row items-center justify-between shrink-0">
          <CardTitle className="text-white flex items-center gap-2">
            <Users className="w-5 h-5 text-cyan-400" />
            Friends
            {requests.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-xs bg-red-500 rounded-full">{requests.length}</span>
            )}
          </CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-friends-panel">
            <X className="w-5 h-5" />
          </Button>
        </CardHeader>

        {isGuest ? (
          <CardContent className="text-center py-8">
            <Users className="w-10 h-10 text-white/30 mx-auto mb-3" />
            <p className="text-white/50 text-sm">Create an account to add friends</p>
          </CardContent>
        ) : (
          <>
            {/* Tabs */}
            <div className="flex border-b border-white/10 px-4 shrink-0">
              {[
                { id: 'friends', label: 'Friends', count: friends.length },
                { id: 'requests', label: 'Requests', count: requests.length },
                { id: 'add', label: 'Add Friend', count: null }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-3 py-2.5 text-xs font-medium transition-colors relative ${
                    activeTab === tab.id ? 'text-cyan-400' : 'text-white/50 hover:text-white/80'
                  }`}
                  data-testid={`friends-tab-${tab.id}`}
                >
                  {tab.label}
                  {tab.count > 0 && (
                    <span className="ml-1 px-1 py-0.5 text-[10px] bg-cyan-500/20 text-cyan-300 rounded">{tab.count}</span>
                  )}
                  {activeTab === tab.id && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-cyan-400" />
                  )}
                </button>
              ))}
            </div>

            <CardContent className="flex-1 overflow-y-auto space-y-2 p-4">
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : activeTab === 'friends' ? (
                friends.length === 0 ? (
                  <div className="text-center py-8">
                    <UserPlus className="w-8 h-8 text-white/20 mx-auto mb-2" />
                    <p className="text-white/40 text-sm">No friends yet</p>
                    <p className="text-white/30 text-xs mt-1">Click on players in the club to add them!</p>
                  </div>
                ) : (
                  friends.map(friend => (
                    <div
                      key={friend.friendship_id}
                      className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                      data-testid={`friend-${friend.user_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-cyan-500/30 to-purple-500/30 flex items-center justify-center text-sm font-bold">
                            {friend.name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <Circle
                            className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 ${
                              isOnline(friend.user_id) ? 'text-green-400 fill-green-400' : 'text-gray-500 fill-gray-500'
                            }`}
                          />
                        </div>
                        <div>
                          <p className="text-white text-sm font-medium">{friend.name}</p>
                          <p className="text-white/40 text-xs">
                            {isOnline(friend.user_id)
                              ? `Online - ${getOnlineRoom(friend.user_id) || 'Nightclub'}`
                              : 'Offline'}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        {isOnline(friend.user_id) && onStartChat && (
                          <Button
                            variant="ghost" size="icon"
                            className="h-8 w-8 text-cyan-400 hover:bg-cyan-500/20"
                            onClick={() => { onStartChat(friend.user_id, friend.name); onClose(); }}
                            data-testid={`chat-friend-${friend.user_id}`}
                          >
                            <MessageCircle className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost" size="icon"
                          className="h-8 w-8 text-red-400 hover:bg-red-500/20"
                          onClick={() => removeFriend(friend.friendship_id)}
                          data-testid={`remove-friend-${friend.user_id}`}
                        >
                          <UserX className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))
                )
              ) : activeTab === 'requests' ? (
                requests.length === 0 ? (
                  <div className="text-center py-8">
                    <Clock className="w-8 h-8 text-white/20 mx-auto mb-2" />
                    <p className="text-white/40 text-sm">No pending requests</p>
                  </div>
                ) : (
                  requests.map(req => (
                    <div
                      key={req.friendship_id}
                      className="flex items-center justify-between p-3 rounded-lg bg-white/5"
                      data-testid={`friend-request-${req.friendship_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-yellow-500/30 to-orange-500/30 flex items-center justify-center text-sm font-bold">
                          {req.user_name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <p className="text-white text-sm font-medium">{req.user_name}</p>
                          <p className="text-white/40 text-xs">Wants to be your friend</p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          className="h-7 px-2 bg-green-600 hover:bg-green-500 text-xs"
                          onClick={() => respondToRequest(req.friendship_id, true)}
                          data-testid={`accept-request-${req.friendship_id}`}
                        >
                          <UserCheck className="w-3 h-3 mr-1" /> Accept
                        </Button>
                        <Button
                          size="sm" variant="ghost"
                          className="h-7 px-2 text-red-400 hover:bg-red-500/20 text-xs"
                          onClick={() => respondToRequest(req.friendship_id, false)}
                          data-testid={`decline-request-${req.friendship_id}`}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))
                )
              ) : (
                /* Add Friend Tab - shows nearby players */
                <div className="space-y-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                    <Input
                      placeholder="Search players..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 bg-white/5 border-white/10 text-white text-sm h-9"
                      data-testid="friend-search-input"
                    />
                  </div>
                  {nearbyPlayers.length === 0 ? (
                    <div className="text-center py-6">
                      <Users className="w-8 h-8 text-white/20 mx-auto mb-2" />
                      <p className="text-white/40 text-sm">No players found nearby</p>
                    </div>
                  ) : (
                    nearbyPlayers.slice(0, 20).map(player => (
                      <div
                        key={player.user_id}
                        className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                        data-testid={`add-player-${player.user_id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-500/30 to-pink-500/30 flex items-center justify-center text-sm font-bold">
                            {player.name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <p className="text-white text-sm">{player.name}</p>
                        </div>
                        <Button
                          size="sm"
                          className="h-7 px-2 bg-cyan-600 hover:bg-cyan-500 text-xs"
                          onClick={() => sendFriendRequest(player.user_id)}
                          data-testid={`send-request-${player.user_id}`}
                        >
                          <UserPlus className="w-3 h-3 mr-1" /> Add
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              )}
            </CardContent>
          </>
        )}
      </Card>
    </div>
  );
};

export default FriendsPanel;
